import 'amazon-connect-streams';
import { useEffect, useRef } from 'react';
import { useAppContext } from '../../../AppContext';
import { useChatContext } from '../../../contexts/ChatContext';
import { Page } from '../../../models/Page';

interface UseChatLifecycleProps {
  selectedChatRef: React.MutableRefObject<string | null>;
  chatContactsRef: React.MutableRefObject<string[]>;
  onViewContact?: (contactId: string | undefined) => void;
}

export function useChatLifecycle({ selectedChatRef, chatContactsRef, onViewContact }: UseChatLifecycleProps): void {
  const { setActivePage } = useAppContext();
  const { selectedChat, setSelectedChat, setChatContacts, setPreviousChatMessagesByContact } = useChatContext();
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    connect.contact(contact => {
      if (contact.getType() !== connect.ContactType.CHAT) {
        return;
      }

      contact.onConnected(() => {
        const currentContacts = chatContactsRef.current;
        if (currentContacts?.length === 1 && !selectedChat) {
          setSelectedChat(currentContacts[0]);
        }
      });

      contact.onConnecting(() => {
        setActivePage(Page.CHAT);

        setChatContacts(prevContacts => {
          if (!prevContacts.includes(contact.contactId)) {
            return [...prevContacts, contact.contactId];
          }
          return prevContacts;
        });
      });

      contact.onDestroy(() => {
        setChatContacts(prevContacts => {
          const newContacts = prevContacts.filter(contactId => contactId !== contact.contactId);

          if (newContacts.length !== 1) {
            setSelectedChat(null);
          } else {
            setSelectedChat(newContacts[0]);
          }

          return newContacts;
        });

        setPreviousChatMessagesByContact(prev => {
          const updatedMessages = { ...prev };
          delete updatedMessages[contact.contactId];
          return updatedMessages;
        });

        if (onViewContact) onViewContact(undefined);
      });
    });
  }, [
    chatContactsRef,
    onViewContact,
    selectedChat,
    setActivePage,
    setChatContacts,
    setPreviousChatMessagesByContact,
    setSelectedChat
  ]);

  // Sync selected chat ref
  useEffect(() => {
    if (selectedChat) {
      connect.core.viewContact(selectedChat);
    }
    selectedChatRef.current = selectedChat;
  }, [selectedChat, selectedChatRef]);
}
